/**
 * IntegrationHub - Connect external apps to AI agents
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Link2, Mail, Calendar, FileText, MessageSquare,
  CheckCircle, Loader2, Plus, X, ExternalLink, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEGRATION_ICONS = {
  google: Mail,
  slack: MessageSquare,
  notion: FileText
};

const INTEGRATION_COLORS = {
  google: 'from-red-500/20 to-orange-500/20 border-red-500/30 text-red-400',
  slack: 'from-purple-500/20 to-pink-500/20 border-purple-500/30 text-purple-400',
  notion: 'from-gray-500/20 to-zinc-500/20 border-gray-500/30 text-gray-400'
};

export default function IntegrationHub() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth?redirect=/integrations');
      return;
    }
    fetchData();
  }, [user, token]);

  const fetchData = async () => {
    try {
      const [availableRes, connectedRes] = await Promise.all([
        axios.get(`${API}/integrations/available`),
        axios.get(`${API}/integrations/connected`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setIntegrations(availableRes.data.integrations);
      setConnections(connectedRes.data.connections);
    } catch (err) {
      console.error('Failed to fetch integrations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (provider) => {
    setConnecting(provider);
    try {
      const response = await axios.post(`${API}/integrations/connect/${provider}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.auth_url) {
        // Redirect to OAuth
        window.location.href = response.data.auth_url;
      } else {
        toast.error('Failed to initiate connection');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Connection failed';
      toast.error(detail);
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider}? AI agents will no longer be able to access this service.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/integrations/disconnect/${provider}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${provider} disconnected`);
      fetchData();
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  const isConnected = (provider) => {
    return connections.some(c => c.provider === provider);
  };

  const getConnection = (provider) => {
    return connections.find(c => c.provider === provider);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Link2 className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Integration Hub</h1>
          <p className="text-zinc-400 max-w-xl mx-auto">
            Connect your apps and let AI agents work with your real data.
            Read emails, check calendar, send messages - all through AI.
          </p>
        </motion.div>

        {/* What You Can Do */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-6 mb-8"
        >
          <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            What AI Can Do With Your Connected Apps
          </h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-red-400 mt-0.5" />
              <div>
                <span className="text-white">"Summarize my unread emails"</span>
                <p className="text-zinc-500">AI reads your actual Gmail</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Calendar className="w-4 h-4 text-blue-400 mt-0.5" />
              <div>
                <span className="text-white">"What's on my calendar tomorrow?"</span>
                <p className="text-zinc-500">AI checks your real schedule</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MessageSquare className="w-4 h-4 text-purple-400 mt-0.5" />
              <div>
                <span className="text-white">"Post update to #team channel"</span>
                <p className="text-zinc-500">AI sends to your Slack</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <span className="text-white">"Add this to my Notion notes"</span>
                <p className="text-zinc-500">AI writes to your workspace</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Integrations Grid */}
        <div className="space-y-4">
          {integrations.map((integration, index) => {
            const Icon = INTEGRATION_ICONS[integration.id] || Link2;
            const colors = INTEGRATION_COLORS[integration.id] || 'from-teal-500/20 to-blue-500/20 border-teal-500/30 text-teal-400';
            const connected = isConnected(integration.id);
            const connection = getConnection(integration.id);

            return (
              <motion.div
                key={integration.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                className={`bg-gradient-to-r ${colors} border rounded-xl p-6`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-zinc-900/50 rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        {integration.name}
                        {connected && (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        )}
                      </h3>
                      <p className="text-zinc-400 text-sm mb-2">{integration.description}</p>
                      
                      {connected && connection && (
                        <p className="text-sm text-green-400">
                          Connected as {connection.email}
                        </p>
                      )}
                      
                      {!connected && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {integration.capabilities?.map((cap, i) => (
                            <span key={i} className="text-xs bg-zinc-900/50 text-zinc-400 px-2 py-1 rounded">
                              {cap}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    {connected ? (
                      <Button
                        onClick={() => handleDisconnect(integration.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Disconnect
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleConnect(integration.id)}
                        disabled={connecting === integration.id}
                        className="bg-white/10 hover:bg-white/20 text-white"
                      >
                        {connecting === integration.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4 mr-2" />
                        )}
                        Connect
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Coming Soon */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center"
        >
          <p className="text-zinc-500 text-sm">
            More integrations coming soon: Microsoft 365, HubSpot, Shopify, Twitter, LinkedIn
          </p>
        </motion.div>

        {/* Setup Required Notice */}
        {integrations.length > 0 && !connections.length && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6"
          >
            <h3 className="text-yellow-400 font-semibold mb-2">Setup Required</h3>
            <p className="text-zinc-400 text-sm">
              Google integration requires OAuth credentials. Add GOOGLE_CLIENT_ID and 
              GOOGLE_CLIENT_SECRET to your environment variables.
            </p>
            <a 
              href="https://console.cloud.google.com/apis/credentials" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-yellow-400 hover:text-yellow-300 text-sm mt-2"
            >
              Get credentials from Google Cloud Console
              <ExternalLink className="w-3 h-3" />
            </a>
          </motion.div>
        )}
      </div>
    </div>
  );
}
